import type { Bundle, Practitioner, Reference } from '@medplum/fhirtypes'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  type CriticalAttentionFlagDetails,
  PatientFlag,
} from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button, cn, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import type { JSX } from 'react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import {
  criticalAttentionTimelineEventMessage,
  mergeCriticalAttentionDetails,
} from '#src/components/critical-attention/criticalAttentionFlag.ts'
import { useCriticalAttentionFlagActivity } from '#src/components/critical-attention/UseCriticalAttentionFlagActivity.tsx'
import { OOSA_HISTORY_DIALOG_HEIGHT_PX } from '#src/components/oosa/OosaFlagDialog.tsx'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

const criticalAttentionFlagFormSchema = z
  .object({
    reason: z.string(),
    notes: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!patients.parseStoredCriticalAttentionReason(data.reason)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reason is required',
        path: ['reason'],
      })
    }
  })

interface CriticalAttentionFlagFormValues {
  reason: string
  notes: string
}

const REASON_OPTIONS = patients.CRITICAL_ATTENTION_REASON_VALUES.map(
  (value) => ({
    value,
    label: patients.CRITICAL_ATTENTION_REASON_LABELS[value],
  })
)

function reasonOptionsFor(reason: string): { value: string; label: string }[] {
  const parsed = patients.parseStoredCriticalAttentionReason(reason)
  if (
    !parsed ||
    (patients.CRITICAL_ATTENTION_REASON_VALUES as readonly string[]).includes(
      parsed
    )
  ) {
    return REASON_OPTIONS
  }
  return [
    ...REASON_OPTIONS,
    {
      value: parsed,
      label: patients.CRITICAL_ATTENTION_REASON_LABELS[parsed],
    },
  ]
}

const EMPTY_VALUES: CriticalAttentionFlagFormValues = {
  reason: '',
  notes: '',
}

type CriticalAttentionDialogTab = 'update' | 'history'
type CriticalAttentionDialogMode = 'create' | 'edit'

export interface CriticalAttentionFlagDialogProps {
  participant: Participant
  author: Reference<Practitioner>
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: CriticalAttentionDialogMode
  existingFlag?: PatientFlag
  flagsToReplace?: readonly PatientFlag[]
  historyFlagIds?: readonly string[]
  historyFlags?: readonly PatientFlag[]
  initialDetails?: CriticalAttentionFlagDetails
  onSaved?: (details: CriticalAttentionFlagDetails) => void
  onPersistedFlag?: (flag: PatientFlag) => void
  onSaveFailed?: () => void
  defaultTab?: CriticalAttentionDialogTab
}

export function CriticalAttentionFlagDialog({
  participant,
  author,
  open,
  onOpenChange,
  mode = 'create',
  existingFlag,
  flagsToReplace,
  historyFlagIds,
  historyFlags,
  initialDetails,
  onSaved,
  onPersistedFlag,
  onSaveFailed,
  defaultTab = 'update',
}: CriticalAttentionFlagDialogProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const transactionMutation = useTransactionMutation()
  const form = useForm({
    schema: criticalAttentionFlagFormSchema,
    defaultValues: formValues(mode, existingFlag, initialDetails),
  })
  const [tab, setTab] = useState<CriticalAttentionDialogTab>(defaultTab)
  const existingFlagId = existingFlag?.toFhirResource().id
  const fallbackFlags = [
    ...(historyFlags ?? []),
    ...(existingFlag ? [existingFlag] : []),
  ]
  const activityFlagIds = [
    ...new Set([
      ...(historyFlagIds ?? []),
      ...fallbackFlags.flatMap((flag) => {
        const id = flag.toFhirResource().id
        return id != null ? [id] : []
      }),
      ...(existingFlagId ? [existingFlagId] : []),
    ]),
  ]
  const activity = useCriticalAttentionFlagActivity(
    open ? activityFlagIds : undefined,
    open ? fallbackFlags : []
  )

  const resetSourceRef = useRef({
    existingFlag,
    initialDetails,
    defaultTab,
  })
  resetSourceRef.current = { existingFlag, initialDetails, defaultTab }

  useEffect(() => {
    const {
      existingFlag: flag,
      initialDetails: details,
      defaultTab: tabOnOpen,
    } = resetSourceRef.current
    if (!open || mode === 'create') {
      form.reset(EMPTY_VALUES)
      if (open) {
        setTab(tabOnOpen)
      }
      return
    }
    form.reset(formValues(mode, flag, details))
    setTab(tabOnOpen)
  }, [open, mode, form])

  const handleSubmit = form.handleSubmit((values) => {
    const reason = values.reason as patients.CriticalAttentionReason
    const existingDetails = mergeCriticalAttentionDetails(
      existingFlag?.criticalAttentionDetails,
      initialDetails
    )
    const details: CriticalAttentionFlagDetails = {
      setOn: existingDetails?.setOn ?? new Date().toISOString(),
      reason,
      notes: values.notes.trim(),
    }
    onSaved?.(details)
    onOpenChange(false)
    void transactionMutation.mutateAsync(
      (builder) => {
        if (mode === 'edit' && existingFlag) {
          builder.patch(existingFlag, (flag) => {
            flag.applyCriticalAttentionDetails(details)
            flag.status = 'active'
          })
        } else {
          for (const flag of flagsToReplace ?? []) {
            builder.patch(flag, (inactive) => {
              inactive.status = 'inactive'
            })
          }
          const created = PatientFlag.build({
            patient: participant.reference,
            author,
            flagCode: patients.ParticipantFlagCode.CRITICAL_ATTENTION,
            reason: patients.formatStoredCriticalAttentionReason(
              details.reason
            ),
            notes: details.notes,
          })
          created.applyCriticalAttentionDetails(details)
          builder.create(created)
        }
        builder.create(
          new CommentCommunication({
            partOf: [participant.reference],
            message: criticalAttentionTimelineEventMessage(
              details,
              mode === 'edit' ? 'modified' : 'set on'
            ),
            sender: author,
          })
        )
      },
      {
        onError: () => onSaveFailed?.(),
        onSuccess: (bundle) => {
          for (const flag of patientFlagsFromBundle(bundle)) {
            onPersistedFlag?.(flag)
          }
        },
      }
    )
  })

  const formBody = (
    <Form onSubmit={handleSubmit} className="flex flex-col">
      <DialogContent className="gap-6 px-6 pt-6">
        <FormField
          control={form.control}
          name="reason"
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <div className="flex flex-col gap-1">
              <FormLabel className="text-sm font-semibold">Reason</FormLabel>
              <ComboboxButton
                options={reasonOptionsFor(value)}
                value={
                  patients.parseStoredCriticalAttentionReason(value) ??
                  undefined
                }
                onChange={onChange}
                placeholder="Please select one..."
                className="w-full justify-between"
                {...fieldProps}
              />
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <div className="flex flex-col gap-1">
              <FormLabel className="text-sm font-semibold">Notes</FormLabel>
              <Textarea
                {...fieldProps}
                value={value}
                onChange={onChange}
                rows={4}
                className="min-h-[88px]"
              />
            </div>
          )}
        />
      </DialogContent>
      <DialogFooter className="gap-4 p-6 pt-0 sm:gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={transactionMutation.isPending}
          disabled={isReadOnly}
        >
          Confirm
        </Button>
      </DialogFooter>
    </Form>
  )

  const isHistoryTab = tab === 'history'
  const historyFrameStyle = isHistoryTab
    ? {
        height: OOSA_HISTORY_DIALOG_HEIGHT_PX,
        maxHeight: OOSA_HISTORY_DIALOG_HEIGHT_PX,
      }
    : undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay
        className="h-auto max-w-[520px] auto-rows-auto"
        notScrollable
      >
        <div
          data-critical-attention-dialog-tab={tab}
          className={cn(
            'flex min-h-0 flex-col',
            isHistoryTab && 'overflow-hidden'
          )}
          style={historyFrameStyle}
        >
          <div
            data-fake-focus-trap="true"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on dialog open so no visible field shows a focus ring; Tab moves on to the real first focusable child
            tabIndex={0}
            className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
          />
          <Tabs
            value={tab}
            onValueChange={(value) => {
              if (value === 'update' || value === 'history') {
                setTab(value)
              }
            }}
            className={cn(
              'flex min-h-0 flex-col',
              isHistoryTab && 'h-full overflow-hidden'
            )}
          >
            <DialogHeader className="shrink-0 flex-col justify-start gap-4 pb-0">
              <DialogTitle className="text-xl font-semibold tracking-tight">
                Critical Attention
              </DialogTitle>
              <TabsList variant="underline" className="w-full justify-start">
                <TabsTrigger value="update">Update</TabsTrigger>
                <TabsTrigger value="history">
                  History{activity.length > 0 && ` · ${activity.length}`}
                </TabsTrigger>
              </TabsList>
            </DialogHeader>
            <TabsContent
              value="update"
              className="min-h-0 flex-1 overflow-y-auto"
            >
              {formBody}
            </TabsContent>
            <TabsContent
              value="history"
              className="mt-0 min-h-0 flex-1 overflow-hidden"
            >
              <ScrollArea className="h-full" scrollRestorationId={null}>
                <DialogContent>
                  {activity.length > 0 ? (
                    <ActivityList>{activity}</ActivityList>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No changes recorded yet.
                    </span>
                  )}
                </DialogContent>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </DialogDisplay>
    </Dialog>
  )
}

function formValues(
  mode: CriticalAttentionDialogMode,
  flag: PatientFlag | undefined,
  fallback?: CriticalAttentionFlagDetails
): CriticalAttentionFlagFormValues {
  if (mode === 'create') {
    return EMPTY_VALUES
  }
  const details = mergeCriticalAttentionDetails(
    flag?.criticalAttentionDetails,
    fallback
  )
  if (!details) {
    return EMPTY_VALUES
  }
  return {
    reason: details.reason,
    notes: details.notes,
  }
}

function patientFlagsFromBundle(bundle: Bundle | undefined): PatientFlag[] {
  return (bundle?.entry ?? []).flatMap((entry) => {
    const resource = entry.resource
    if (!resource || !PatientFlag.isInstance(resource)) {
      return []
    }
    return [PatientFlag.fromFhirResource(resource)]
  })
}
